import {
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';
import ToastService from '~/services/toast.service';

import { queryKeysDeliveryNote } from './queryKeys';

type MutationRequestSignatureArguments = {
  assetIds: UUID[];
  userIds: UUID[];
};

type RequestSignatureResponse = {
  code: string;
  message: string;
  requestId: string;
};

type RequestSignatureMutationOptions = UseMutationOptions<
  RequestSignatureResponse,
  Error,
  MutationRequestSignatureArguments
>;

/**
 * Requests signatures for delivery notes.
 *
 * @param assetIds - The delivery note IDs.
 * @param userIds - The user IDs.
 * @returns A promise that resolves with the API response.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Notification/operation/request_signature_push_request_signature_post
 */
export const requestSignature = async (assetIds: UUID[], userIds: UUID[]) => {
  const response = await vestigasApi
    .post(ENDPOINT.DELIVERY_NOTE.REQUEST_SIGNATURE(), {
      json: {
        assetIds,
        recipients: userIds,
      },
    })
    .json<RequestSignatureResponse>();

  return response;
};

/**
 * Custom hook for requesting signatures for delivery notes.
 *
 * @returns An object containing the mutation function and related properties.
 */
export const useMutationRequestSignature = (
  options?: RequestSignatureMutationOptions,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      assetIds,
      userIds,
    }: MutationRequestSignatureArguments) =>
      requestSignature(assetIds, userIds),
    onSuccess() {
      ToastService.success([
        'Die Signaturen wurden angefordert.',
        'Die ausgewählten Benutzer wurden benachrichtigt.',
      ]);

      void queryClient.invalidateQueries({
        queryKey: [queryKeysDeliveryNote.base()],
      });
    },
    ...options,
  });
};
